import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import VerticalSplitOutlined from '@mui/icons-material/VerticalSplitOutlined';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { statusTone } from '../status';
import { STAGES, problemCounts } from './editorModel';

// New-UI header for InvoiceDocumentEditor (concept A, docs/ui_redesign/13-invoice-editor-cockpit.md): what the invoice is,
// where it is in its life, and the way to its history. Everything comes from the editor's state; the
// buttons call the editor's own handlers.
function Stepper({ stage }) {
	if (!stage) return null;
	return (
		<ol className="apw2-stepper" aria-label="Invoice progress">
			{STAGES.map((label, i) => {
				const done = stage.done || i < stage.current;
				const cur = !stage.done && i === stage.current;
				const cls = done ? 'done' : cur ? (stage.failed ? 'cur failed' : 'cur') : '';
				const text = cur && i === 2 && stage.level ? `Approve L${stage.level} of ${stage.levels}` : cur && stage.failed ? 'Post failed' : cur && stage.returned ? 'Correct (returned)' : label;
				return (
					<li key={label} className={cls} aria-current={cur ? 'step' : undefined}>
						<span className="d" aria-hidden="true">{done ? <CheckRounded fontSize="inherit" /> : null}</span>{text}
					</li>
				);
			})}
		</ol>
	);
}

export default function EditorHeader({
	moduleLabel, modeTitle, invoiceNumber, statusText, statusId, stage, flags = [], viewerOpen, onToggleViewer, onActivity, onClose
}) {
	const tone = statusTone(statusId);
	return (
		<header className="apw2-ed-head">
			<button type="button" className="apw2-iconbtn lg" aria-label="Close" title="Close" onClick={onClose}><CloseIcon fontSize="inherit" /></button>
			<div className="apw2-ed-title">
				<span className="apw2-ed-eyebrow">{moduleLabel} · {/test\)?$/i.test(modeTitle || '') ? 'Preview of this mapping' : modeTitle}</span>
				<h1>{invoiceNumber || 'New invoice'}</h1>
			</div>
			{statusText ? <span className="apw2-status"><span className={'apw2-chip t-' + tone} title={statusId ? `Status ${statusId}` : undefined}><span className="d" />{statusText}</span></span> : null}
			<Stepper stage={stage} />
			<span className="apw2-grow" />
			{flags.map(f => <span key={f.key} className={'apw2-chip t-' + f.tone} title={f.title || ''}>{f.label}</span>)}
			{onActivity && <button type="button" className="apw2-btn" onClick={onActivity}><HistoryIcon fontSize="inherit" />Activity</button>}
			{onToggleViewer && (
				<button type="button" className="apw2-btn" aria-pressed={!!viewerOpen} onClick={onToggleViewer}>
					<VerticalSplitOutlined fontSize="inherit" />{viewerOpen ? 'Hide document' : 'Show document'}
				</button>
			)}
		</header>
	);
}

// "Needs attention": what stops Save (errors) and what Save will ask about (warnings), each with its fix or a jump.
// It is the editor's own list, shown live. Nothing to show means nothing is drawn.
export function EditorProblems({ problems = [], onJump, onFix }) {
	if (!problems.length) return null;
	const { blocking, review } = problemCounts(problems);
	const errors = problems.some(p => p.tone === 'bad');
	const rank = p => (p.blocks ? 0 : p.tone === 'bad' ? 1 : 2);
	const ordered = [...problems].sort((a, b) => rank(a) - rank(b));
	const sub = [blocking ? `${blocking} ${blocking === 1 ? 'stops' : 'stop'} Save` : '', review ? `${review} to review` : ''].filter(Boolean).join(' · ');
	const guideOnly = problems.every(p => p.tone === 'info');
	return (
		<section className={'apw2-edcard apw2-ed-attn' + (errors ? ' t-bad' : '')} role="status" aria-label="Problems on this invoice">
			<div className="apw2-edcard-h">
				<span className={'apw2-ed-attn-dot' + (errors ? ' bad' : '')} aria-hidden="true" />
				<h2>{guideOnly ? 'Next step' : 'Needs attention'}</h2>
				<span className="apw2-edcard-sub">{sub}</span>
			</div>
			<ul className="apw2-ed-attn-list">
				{ordered.map(p => (
					<li key={p.key} className={p.tone === 'bad' ? 'bad' : p.tone === 'info' ? 'info' : 'warn'}>
						<span className="ic" aria-hidden="true">{p.tone === 'bad' ? <ErrorOutlineRounded fontSize="inherit" /> : p.tone === 'info' ? <InfoOutlined fontSize="inherit" /> : <WarningAmberRounded fontSize="inherit" />}</span>
						<span className="tx">{p.text}</span>
						<span className="acts">
							{p.fix && onFix && <button type="button" className="apw2-linkbtn" onClick={() => onFix(p.fix)}>{p.fix.label}</button>}
							{p.jump && <button type="button" className="apw2-linkbtn" onClick={() => onJump(p.jump)}>{p.jump === 'lines' ? 'Go to lines' : 'Go to field'}</button>}
						</span>
					</li>
				))}
			</ul>
		</section>
	);
}

// A card around a block of the classic editor in the new UI. In Classic it renders its children and nothing else, so
// Classic's HTML is unchanged.
export function EditorCard({ enabled, className = '', title, sub, tools, children, id }) {
	if (!enabled) return <>{children}</>;
	return (
		<section className={'apw2-edcard ' + className} aria-label={typeof title === 'string' ? title : undefined} id={id}>
			<div className="apw2-edcard-h">
				<h2>{title}</h2>
				{sub ? <span className="apw2-edcard-sub">{sub}</span> : null}
				<span className="apw2-grow" />
				{tools}
			</div>
			<div className="apw2-edcard-b">{children}</div>
		</section>
	);
}
